import React, { useState, useEffect } from 'react'
import styles from './styles.module.scss'
import api from '../../../helpers/apiRequester'
import { Loading, Report } from 'notiflix'
import { useApprovedLenders } from '../../../helpers/approvedLenders'
import { useAuthUserData } from '../../../authUserProvider'

const BASE_URL =
    process.env.REACT_APP_API_URL == 'http://localhost:8080/api'
        ? 'http://localhost:8080'
        : 'https://case-delivery.sentinellegal.co.uk'

interface ClientGroupConfig {
    batchName: string
    introducer: string
    limit: number | null
    sDate: string
    eDate: string
    lenders: string[]
}

interface FormErrors {
    batchName?: string
    introducer?: string
    limit?: string
    sDate?: string
    eDate?: string
    lenders?: string
}

interface IntroducerOption {
    value: string
    label: string
    availableClients: number
}

export const ILAddClientsView = () => {
    const authUserData = useAuthUserData()
    // Approved lenders come from the centralised list (Admin > Approved Lenders).
    const { options: lenderOptions, loading: lendersLoading } = useApprovedLenders()
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState<ClientGroupConfig>({
        batchName: '',
        introducer: '',
        limit: null,
        sDate: '',
        eDate: '',
        lenders: [],
    })
    const [errors, setErrors] = useState<FormErrors>({})
    const [success, setSuccess] = useState(false)
    const [result, setResult] = useState<any>(null)
    const [lenderSearch, setLenderSearch] = useState('')
    const [authLoading, setAuthLoading] = useState(true)
    const [authError, setAuthError] = useState(false)
    const [introducers, setIntroducers] = useState<IntroducerOption[]>([])
    const [introducersLoading, setIntroducersLoading] = useState(false)
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

    // Authentication check on component mount
    useEffect(() => {
        const checkAuthentication = async () => {
            try {
                setAuthLoading(true)
                setAuthError(false)

                const response = await api.post('/il-add-client-auth-check')

                if (response.data === 'SUCCESS') {
                    setAuthLoading(false)
                } else {
                    setAuthError(true)
                    setAuthLoading(false)
                }
            } catch (error) {
                console.error('Authentication check failed:', error)
                setAuthError(true)
                setAuthLoading(false)

                // Show authentication error notification
                Report.failure(
                    'Authentication Required',
                    'Your session has expired. Please log in again to continue.',
                    'Okay'
                )
            }
        }

        checkAuthentication()
    }, [])

    // Fetch introducers with client counts
    useEffect(() => {
        const fetchIntroducers = async () => {
            try {
                setIntroducersLoading(true)
                const response = await api.post('/il-introducers-with-counts')

                if (response.data && response.data.success) {
                    setIntroducers(response.data.introducers)
                } else {
                    console.error('Failed to fetch introducers:', response.data?.message)
                    // Fallback to static data if API fails
                    setIntroducers([
                        { value: 'ADL', label: 'All Day Loans', availableClients: 0 },
                        { value: 'MR', label: 'CheckFinance', availableClients: 0 },
                        { value: 'SO', label: 'SalesOne', availableClients: 0 },
                        { value: 'SNT', label: 'Sentinel', availableClients: 0 },
                    ])
                    Report.warning('Warning', 'Could not load introducer client counts. Using fallback data.', 'Okay')
                }
            } catch (error) {
                console.error('Error fetching introducers:', error)
                // Fallback to static data if API fails
                setIntroducers([
                    { value: 'ADL', label: 'All Day Loans', availableClients: 0 },
                    { value: 'MR', label: 'CheckFinance', availableClients: 0 },
                    { value: 'SO', label: 'SalesOne', availableClients: 0 },
                    { value: 'SNT', label: 'Sentinel', availableClients: 0 },
                ])
                Report.warning('Warning', 'Could not load introducer client counts. Using fallback data.', 'Okay')
            } finally {
                setIntroducersLoading(false)
            }
        }

        fetchIntroducers()
    }, [])

    const handleInputChange = (field: keyof ClientGroupConfig, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }))

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: undefined,
            }))
        }
    }

    const handleLenderToggle = (lender: string) => {
        setFormData((prev) => ({
            ...prev,
            lenders: prev.lenders.includes(lender)
                ? prev.lenders.filter((l) => l !== lender)
                : [...prev.lenders, lender],
        }))
    }

    const selectAllLenders = () => {
        setFormData((prev) => ({
            ...prev,
            lenders: lenderOptions.map((l) => l.value),
        }))
    }

    const clearAllLenders = () => {
        setFormData((prev) => ({
            ...prev,
            lenders: [],
        }))
    }

    const filteredLenders = lenderOptions.filter((lender) =>
        lender.label.toLowerCase().includes(lenderSearch.toLowerCase())
    )

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {}

        if (!formData.batchName.trim()) {
            newErrors.batchName = 'Batch name is required'
        }

        if (!formData.introducer) {
            newErrors.introducer = 'Please select an introducer'
        }

        if (formData.limit !== null && formData.limit < 1) {
            newErrors.limit = 'Limit must be at least 1 if specified'
        }

        if (formData.sDate && formData.eDate && new Date(formData.sDate) > new Date(formData.eDate)) {
            newErrors.eDate = 'End date must be after start date'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setLoading(true)
        setSuccess(false)
        setResult(null)

        try {
            const response = await api.post(`${BASE_URL}/api/il-add-clients`, {
                clientGroupConfig: formData,
                userEmail: authUserData?.email || 'unknown@sentinellegal.co.uk',
            })

            if (response.data && response.data.success) {
                setSuccess(true)
                setResult(response.data)

                if (response.data.processingInBackground) {
                    Report.success(
                        'Processing Started',
                        `Found ${response.data.clientCount} clients. Processing has started in the background. You will receive an email when complete.`,
                        'Okay'
                    )
                } else {
                    Report.success('Success', 'IL clients added successfully!', 'Okay')
                }
            } else {
                Report.failure('Error', response.data?.message || 'Failed to add IL clients', 'Okay')
            }
        } catch (error: any) {
            console.error('Error adding IL clients:', error)
            Report.failure(
                'Error',
                error.response?.data?.message || error.message || 'An unexpected error occurred',
                'Okay'
            )
        } finally {
            setLoading(false)
        }
    }

    const resetForm = () => {
        setFormData({
            batchName: '',
            introducer: '',
            limit: null,
            sDate: '',
            eDate: '',
            lenders: [],
        })
        setErrors({})
        setSuccess(false)
        setResult(null)
        setShowAdvancedFilters(false)
    }

    return (
        <div className={styles.ilAddClientsView}>
            {/* Authentication Loading */}
            {(authLoading || introducersLoading) && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.spinner}></div>
                        <p>{authLoading ? 'Verifying authentication...' : 'Loading introducers...'}</p>
                    </div>
                </div>
            )}

            {/* Authentication Error */}
            {authError && (
                <div className={styles.authError}>
                    <div className={styles.authErrorContent}>
                        <h3>Authentication Required</h3>
                        <p>Your session has expired. Please log in again to continue.</p>
                        <button onClick={() => (window.location.href = '/login')} className={styles.authButton}>
                            Go to Login
                        </button>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {loading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.loadingSpinner}></div>
                        <p className={styles.loadingText}>Adding IL clients...</p>
                    </div>
                </div>
            )}

            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Add IL Clients</h1>
                    <p className={styles.subtitle}>
                        Configure and add a batch of clients to the irresponsible lending database with open banking
                        onboarding links.
                    </p>
                </div>

                <div className={styles.formSection}>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Batch Name */}
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Batch Name *</label>
                            <p className={styles.fieldDescription}>
                                A unique identifier for this batch of clients (e.g., Q1-2024-Batch)
                            </p>
                            <input
                                type="text"
                                value={formData.batchName}
                                onChange={(e) => handleInputChange('batchName', e.target.value)}
                                className={`${styles.input} ${errors.batchName ? styles.error : ''}`}
                                placeholder="Enter batch name"
                            />
                            {errors.batchName && <span className={styles.errorText}>{errors.batchName}</span>}
                        </div>

                        {/* Introducer Selection */}
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Introducer *</label>
                            <p className={styles.fieldDescription}>
                                Select the source system from which to pull client data
                            </p>
                            <select
                                value={formData.introducer}
                                onChange={(e) => handleInputChange('introducer', e.target.value)}
                                className={`${styles.select} ${errors.introducer ? styles.error : ''}`}
                                disabled={introducersLoading}
                            >
                                <option value="">
                                    {introducersLoading ? 'Loading introducers...' : 'Select an introducer'}
                                </option>
                                {introducers.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label} ({option.availableClients.toLocaleString()} clients available)
                                    </option>
                                ))}
                            </select>
                            {errors.introducer && <span className={styles.errorText}>{errors.introducer}</span>}
                        </div>

                        {/* Client Limit */}
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Client Limit</label>
                            <p className={styles.fieldDescription}>
                                Maximum number of clients to import for each selected lender, or total clients when no
                                lenders are selected (leave blank for no limit)
                            </p>
                            <input
                                type="number"
                                min="1"
                                value={formData.limit || ''}
                                onChange={(e) =>
                                    handleInputChange('limit', e.target.value ? parseInt(e.target.value) : null)
                                }
                                className={`${styles.input} ${errors.limit ? styles.error : ''}`}
                                placeholder="Leave blank for no limit"
                            />
                            {errors.limit && <span className={styles.errorText}>{errors.limit}</span>}
                        </div>

                        {/* Advanced Filters Toggle */}
                        <div className={styles.formGroup}>
                            <div className={styles.advancedFiltersToggle}>
                                <label className={styles.toggleLabel}>
                                    <input
                                        type="checkbox"
                                        checked={showAdvancedFilters}
                                        onChange={(e) => setShowAdvancedFilters(e.target.checked)}
                                        className={styles.toggleCheckbox}
                                    />
                                    <span className={styles.toggleText}>
                                        <svg
                                            className={`${styles.chevronIcon} ${
                                                showAdvancedFilters ? styles.chevronUp : styles.chevronDown
                                            }`}
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M6 9L12 15L18 9"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        Advanced Filters
                                        {(formData.sDate || formData.eDate || formData.lenders.length > 0) && (
                                            <span className={styles.activeIndicator}>●</span>
                                        )}
                                    </span>
                                </label>
                                <p className={styles.fieldDescription}>
                                    {showAdvancedFilters
                                        ? 'Configure date ranges and specific lenders for more targeted client selection'
                                        : 'Click to show optional date range and lender filtering options'}
                                </p>
                            </div>
                        </div>

                        {/* Advanced Filters Section */}
                        {showAdvancedFilters && (
                            <div className={styles.advancedFiltersSection}>
                                {/* Date Range */}
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Date Range</label>
                                    <p className={styles.fieldDescription}>
                                        Time period to search for clients in the selected introducer system (leave blank
                                        for no date filtering)
                                    </p>
                                    <div className={styles.dateRange}>
                                        <input
                                            type="date"
                                            value={formData.sDate}
                                            onChange={(e) => handleInputChange('sDate', e.target.value)}
                                            className={`${styles.dateInput} ${errors.sDate ? styles.error : ''}`}
                                        />
                                        <span className={styles.dateSeparator}>to</span>
                                        <input
                                            type="date"
                                            value={formData.eDate}
                                            onChange={(e) => handleInputChange('eDate', e.target.value)}
                                            className={`${styles.dateInput} ${errors.eDate ? styles.error : ''}`}
                                        />
                                    </div>
                                    {(errors.sDate || errors.eDate) && (
                                        <span className={styles.errorText}>{errors.sDate || errors.eDate}</span>
                                    )}
                                </div>

                                {/* Lender Selection */}
                                <div className={styles.formGroup}>
                                    <div className={styles.lenderHeader}>
                                        <label className={styles.label}>
                                            Lenders ({formData.lenders.length} selected
                                            {lenderSearch && `, ${filteredLenders.length} shown`})
                                        </label>
                                        <div className={styles.lenderActions}>
                                            <button
                                                type="button"
                                                onClick={selectAllLenders}
                                                className={styles.actionButton}
                                            >
                                                Select All
                                            </button>
                                            <button
                                                type="button"
                                                onClick={clearAllLenders}
                                                className={styles.actionButton}
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                    </div>
                                    <p className={styles.fieldDescription}>
                                        Select the lenders whose clients should be included in this batch. If no lenders
                                        are selected, clients will be selected from any lender to meet the limit
                                        requirement.
                                    </p>

                                    {/* Lender Search */}
                                    <div className={styles.lenderSearch}>
                                        <input
                                            type="text"
                                            placeholder="Search lenders..."
                                            value={lenderSearch}
                                            onChange={(e) => setLenderSearch(e.target.value)}
                                            className={styles.searchInput}
                                        />
                                    </div>

                                    <div className={styles.lenderSelection}>
                                        <div className={styles.lenderList}>
                                            {filteredLenders.length > 0 ? (
                                                filteredLenders.map((lender) => (
                                                    <label key={lender.value} className={styles.lenderCheckbox}>
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.lenders.includes(lender.value)}
                                                            onChange={() => handleLenderToggle(lender.value)}
                                                        />
                                                        <span>{lender.label}</span>
                                                    </label>
                                                ))
                                            ) : (
                                                <div className={styles.noResults}>
                                                    <p>
                                                        {lendersLoading
                                                            ? 'Loading approved lenders...'
                                                            : `No lenders found matching "${lenderSearch}"`}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {errors.lenders && <span className={styles.errorText}>{errors.lenders}</span>}
                                </div>
                            </div>
                        )}

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button type="button" onClick={resetForm} className={styles.resetButton} disabled={loading}>
                                Reset Form
                            </button>
                            <button type="submit" className={styles.submitButton} disabled={loading}>
                                {loading ? 'Adding Clients...' : 'Add IL Clients'}
                            </button>
                        </div>
                    </form>

                    {/* Success Message */}
                    {success && result && (
                        <div className={styles.successMessage}>
                            {result.processingInBackground ? (
                                <>
                                    <h3>Processing Started!</h3>
                                    <p>Your IL client batch has been queued for processing.</p>
                                    <div className={styles.resultDetails}>
                                        <p>
                                            <strong>Batch Name:</strong> {formData.batchName}
                                        </p>
                                        <p>
                                            <strong>Introducer:</strong> {formData.introducer}
                                        </p>
                                        <p>
                                            <strong>Client Limit:</strong> {formData.limit || 'No limit'}
                                        </p>
                                        <p>
                                            <strong>Date Range:</strong>{' '}
                                            {formData.sDate && formData.eDate
                                                ? `${formData.sDate} to ${formData.eDate}`
                                                : 'No date filtering'}
                                        </p>
                                        <p>
                                            <strong>Lenders:</strong> {formData.lenders.length} selected
                                        </p>
                                        <p>
                                            <strong>Clients Found:</strong> {result.clientCount || 0}
                                        </p>
                                    </div>
                                    <div className={styles.backgroundProcessingInfo}>
                                        <h4>📧 Email Notification</h4>
                                        <p>
                                            You will receive an email at{' '}
                                            <strong>{authUserData?.email || 'your registered email'}</strong> when
                                            processing is complete with:
                                        </p>
                                        <ul>
                                            <li>Final client count and processing results</li>
                                            <li>Download link for the client report</li>
                                            <li>Open Banking link generation status</li>
                                        </ul>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h3>Success!</h3>
                                    <p>IL clients have been added successfully.</p>
                                    <div className={styles.resultDetails}>
                                        <p>
                                            <strong>Batch Name:</strong> {formData.batchName}
                                        </p>
                                        <p>
                                            <strong>Introducer:</strong> {formData.introducer}
                                        </p>
                                        <p>
                                            <strong>Client Limit:</strong> {formData.limit || 'No limit'}
                                        </p>
                                        <p>
                                            <strong>Date Range:</strong>{' '}
                                            {formData.sDate && formData.eDate
                                                ? `${formData.sDate} to ${formData.eDate}`
                                                : 'No date filtering'}
                                        </p>
                                        <p>
                                            <strong>Lenders:</strong> {formData.lenders.length} selected
                                        </p>
                                        <p>
                                            <strong>Clients Added:</strong> {result.clientsAdded || 0}
                                        </p>
                                        <p>
                                            <strong>Open Banking Links Generated:</strong>{' '}
                                            {result.clientsWithLinks || 0}
                                        </p>
                                        {result.failedLinks > 0 && (
                                            <p>
                                                <strong>Failed Links:</strong> {result.failedLinks}
                                            </p>
                                        )}
                                    </div>

                                    {/* Download Report Button */}
                                    {result.reportDownloadUrl && (
                                        <div className={styles.downloadSection}>
                                            <h4>Download Client Report</h4>
                                            <p>
                                                A CSV report containing all clients with their open banking links has
                                                been generated.
                                            </p>
                                            <a
                                                href={result.reportDownloadUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.downloadButton}
                                            >
                                                📥 Download Report
                                            </a>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
